import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/constants";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Ban, Undo2 } from "lucide-react";
import { LEAD_BLACKLIST } from "@/constants/testIds";

// Blacklisting a lead (POST/DELETE /leads/:id/blacklist). While blacklisted the
// server 403s calls, follow-ups, bookings, phone reveals and status/assignment
// changes — the UI turns those off up front rather than letting them fail.

export const BLACKLISTED_TIP = "Lead is blacklisted";

// Mirrors the backend: anyone but a doctor may blacklist; only managers/admins
// may take a lead off the list.
export const canBlacklistLead = (user) => !!user && user.role !== "doctor";
export const canUnblacklistLead = (user) =>
  ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"].includes(user?.role);

/** Wraps a control that is off while the lead is blacklisted, so its tooltip
 * still shows (a disabled button swallows hover). Renders children as-is when
 * not blocked. */
export function BlockedWhen({ blocked, children, className = "inline-flex" }) {
  if (!blocked) return children;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild><span className={className}>{children}</span></TooltipTrigger>
        <TooltipContent>{BLACKLISTED_TIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Small red badge for list rows; the reason is in its tooltip. */
export function BlacklistedBadge({ lead }) {
  if (!lead?.blacklisted) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700"
            onClick={(e) => e.stopPropagation()} data-testid={LEAD_BLACKLIST.badge}>Blacklisted</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-semibold">Reason</p>
          <p className="whitespace-pre-wrap">{lead.blacklist_reason || "—"}</p>
          <p className="mt-1 text-xs opacity-80">By {lead.blacklisted_by_name || "—"} · {formatDateTime(lead.blacklisted_at)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** The red banner on a blacklisted lead, with "Remove from blacklist" for
 * the roles allowed to. */
export function BlacklistBanner({ lead, canRemove, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!lead?.blacklisted) return null;
  const remove = async () => {
    if (!window.confirm(`Remove "${lead.name}" from the blacklist?`)) return;
    setBusy(true);
    try {
      await api.delete(`/leads/${lead.id}/blacklist`);
      toast.success("Removed from blacklist");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid={LEAD_BLACKLIST.banner}>
      <Ban className="h-4 w-4 shrink-0" />
      <p className="min-w-0 flex-1">
        <span className="font-semibold">Blacklisted</span> — {lead.blacklist_reason || "no reason given"}
        <span className="text-red-700/80"> · by {lead.blacklisted_by_name || "—"} · {formatDateTime(lead.blacklisted_at)}</span>
      </p>
      {canRemove && (
        <Button variant="outline" size="sm" onClick={remove} disabled={busy}
          className="border-red-300 bg-white text-red-700 hover:bg-red-100" data-testid={LEAD_BLACKLIST.removeButton}>
          <Undo2 className="mr-1 h-3.5 w-3.5" /> {busy ? "Removing…" : "Remove from blacklist"}
        </Button>
      )}
    </div>
  );
}

/** The toast after POST /leads/bulk-blacklist. `skipped` includes the
 * already-blacklisted ones; the rest were outside the caller's scope. */
export function bulkBlacklistMessage(res) {
  const done = res?.blacklisted || 0;
  const already = res?.already_blacklisted || 0;
  const notYours = Math.max(0, (res?.skipped || 0) - already);
  return `Blacklisted ${done} lead(s)`
    + (already ? `, ${already} already blacklisted` : "")
    + (notYours ? `, ${notYours} skipped (not yours)` : "");
}

/** Reason (required) → POST /leads/:id/blacklist for one `lead`, or, given a
 * `selection` ({lead_ids} or {select_all, filters} — the standard bulk payload)
 * and its `count`, POST /leads/bulk-blacklist. */
export function BlacklistLeadDialog({ lead, selection, count = 0, open, onClose, onDone }) {
  const bulk = !!selection;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Enter a reason"); return; }
    setSaving(true);
    try {
      if (bulk) {
        const { data } = await api.post("/leads/bulk-blacklist", { ...selection, reason: reason.trim() });
        toast.success(bulkBlacklistMessage(data));
      } else {
        await api.post(`/leads/${lead.id}/blacklist`, { reason: reason.trim() });
        toast.success(`${lead.name || "Lead"} blacklisted`);
      }
      onClose();
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">
            {bulk ? `Blacklist ${count} lead(s)` : `Blacklist ${lead?.name || "lead"}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            {bulk ? "These leads move" : "The lead moves"} to the Blacklisted tab and pending follow-ups are cancelled. Calls, follow-ups,
            bookings, viewing the number and status/owner changes are blocked until a manager removes {bulk ? "them" : "it"} from the blacklist.
            {bulk && " Leads already blacklisted, or not yours, are skipped (at most 500 per go)."}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="lead-blacklist-reason" className="text-xs font-medium text-muted-foreground">Reason *</Label>
            <Textarea id="lead-blacklist-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Why is this lead being blacklisted?" data-testid={LEAD_BLACKLIST.reasonInput} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid={LEAD_BLACKLIST.cancelButton}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !reason.trim()} className="bg-red-600 text-white hover:bg-red-700" data-testid={LEAD_BLACKLIST.confirmButton}>
            {saving ? "Blacklisting…" : "Blacklist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
